<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useConfirm } from "primevue/useconfirm";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { Icon } from "@iconify/vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import ReviewModal from "@/views/management/customerReviews/ReviewModal.vue";
import { useAsync } from "@/composables/api/useAsync.js";
import { useCustomerReview } from "@/composables/model/customerReview/useCustomerReview.js";
import { useAgent } from "@/composables/model/agent/useAgent.js";
import { useUserStore } from "@/stores/user.js";
import { PERMISSIONS } from "@/constant/permissions.js";
import Button from "primevue/button";
import {
  formatReviewTimestamp,
  reviewMatchesQuery,
} from "@/utils/customerReview.js";

const { t } = useI18n();
const confirm = useConfirm();
const userStore = useUserStore();
const canDelete = computed(() => userStore.hasPermission(PERMISSIONS.BIG_BOSS));
const searchQuery = ref("");
const modalVisible = ref(false);
const editingReview = ref(null);

const { reviews, loadReviews, removeReview, deletingId } = useCustomerReview();

const { execute, isLoading, isError, errorMessage } = useAsync(loadReviews, {
  apiResult: true,
});

const { agentLabel, loadAgents } = useAgent();

const filteredReviews = computed(() =>
  reviews.value.filter((review) =>
    reviewMatchesQuery(review, searchQuery.value),
  ),
);

function optionText(group, value) {
  if (!value) return "";
  const key = `customerSession.options.${group}.${value}`;
  const translated = t(key);
  return translated === key ? value : translated;
}

function clientLabel(review) {
  return review.client?.name || review.clientID;
}

function openCreate() {
  editingReview.value = null;
  modalVisible.value = true;
}

function openDetails(review) {
  editingReview.value = review;
  modalVisible.value = true;
}

function openEdit(event) {
  openDetails(event.data);
}

function confirmDelete(review) {
  if (!review || deletingId.value !== null) return;
  confirm.require({
    group: "templating",
    header: t("customerSession.delete.header"),
    message: {
      title: t("customerSession.delete.title", { id: review.id }),
      text: t("customerSession.delete.text"),
    },
    icon: "pi pi-exclamation-circle",
    rejectProps: {
      class: "!border-slate-500 !py-2 !px-4 !text-slate-300",
      label: t("message.cancel"),
      outlined: true,
      size: "small",
    },
    acceptProps: {
      class:
        "!border-red-500 !py-2 !px-4 !text-red-400 hover:!bg-red-500 hover:!text-slate-200",
      outlined: true,
      label: t("message.delete"),
      size: "small",
    },
    accept: () =>
      removeReview(review.sessionID).then((result) => {
        if (result?.success) modalVisible.value = false;
      }),
  });
}

onMounted(() => {
  execute();
  loadAgents();
});
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs />
        <AllButtons
          :buttonForNewItem="true"
          :titleForNewItem="t('customerSession.new')"
          :showFilter="false"
          :showCategoriesButtons="false"
          :copyButton="false"
          v-model="searchQuery"
          :permissionForNewItem="PERMISSIONS.CUSTOMER_SESSIONS"
          @buttonForNewItemClicked="openCreate"
        />
      </div>

      <div v-if="isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12" />
      </div>

      <div
        v-else-if="isError"
        class="flex flex-col items-center justify-center gap-3 h-60"
      >
        <p class="text-red-300 text-sm">
          {{ errorMessage || t("customerSession.api.loadError") }}
        </p>
        <button
          type="button"
          class="btn btn-outline-dark py-1 text-xs"
          @click="execute()"
        >
          {{ t("customerSession.retry") }}
        </button>
      </div>

      <DataTable
        v-else
        :value="filteredReviews"
        scrollable
        scrollHeight="flex"
        resizableColumns
        removableSort
        stripedRows
        class="text-xs w-full"
        size="small"
        tableStyle="width: 100%; table-layout: fixed;"
        row-hover
        @row-click="openEdit"
        :rowClass="
          () =>
            'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300 cursor-pointer'
        "
      >
        <Column
          field="sessionID"
          :header="t('customerSession.fields.sessionId')"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 8%; min-width: 90px; height: 30px"
        />
        <Column
          :header="t('customerSession.fields.client')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 10%; min-width: 110px; height: 30px"
        >
          <template #body="slotProps">
            {{ clientLabel(slotProps.data) }}
          </template>
        </Column>
        <Column
          :header="t('customerSession.fields.type')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 8%; min-width: 90px; height: 30px"
        >
          <template #body="slotProps">{{
            optionText("type", slotProps.data.type)
          }}</template>
        </Column>
        <Column
          :header="t('customerSession.fields.section')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 8%; min-width: 90px; height: 30px"
        >
          <template #body="slotProps">{{
            optionText("section", slotProps.data.section)
          }}</template>
        </Column>
        <Column
          field="game"
          :header="t('customerSession.fields.game')"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 10%; min-width: 110px; height: 30px"
        />
        <Column
          :header="t('customerSession.fields.openedAt')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 10%; min-width: 120px; height: 30px"
        >
          <template #body="slotProps">{{
            formatReviewTimestamp(slotProps.data.openedAt)
          }}</template>
        </Column>
        <Column
          :header="t('customerSession.fields.agent')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 10%; min-width: 110px; height: 30px"
        >
          <template #body="slotProps">{{
            agentLabel(slotProps.data.agentID)
          }}</template>
        </Column>
        <Column
          :header="t('customerSession.fields.firstReply')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 10%; min-width: 120px; height: 30px"
        >
          <template #body="slotProps">{{
            formatReviewTimestamp(slotProps.data.firstReply)
          }}</template>
        </Column>
        <Column
          :header="t('customerSession.fields.request')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 10%; min-width: 120px; height: 30px"
        >
          <template #body="slotProps">{{
            optionText("request", slotProps.data.requestKind)
          }}</template>
        </Column>
        <Column
          :header="t('customerSession.fields.outcome')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 8%; min-width: 100px; height: 30px"
        >
          <template #body="slotProps">{{
            optionText("outcome", slotProps.data.outcome)
          }}</template>
        </Column>
        <Column
          :header="t('customerSession.fields.lossReason')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 10%; min-width: 120px; height: 30px"
        >
          <template #body="slotProps">{{
            optionText("lossReason", slotProps.data.lossReason)
          }}</template>
        </Column>
        <Column
          :header="t('customerSession.fields.complaintReason')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 10%; min-width: 120px; height: 30px"
        >
          <template #body="slotProps">{{
            optionText("complaintReason", slotProps.data.complaintReason)
          }}</template>
        </Column>
        <Column
          field="note"
          :header="t('customerSession.fields.note')"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          style="width: 14%; min-width: 140px; height: 30px"
        />
        <Column
          :header="t('customerSession.actions')"
          style="width: 16%; min-width: 180px; height: 30px"
        >
          <template #body="slotProps">
            <div class="flex items-center gap-2">
              <Button
                type="button"
                size="small"
                outlined
                icon="pi pi-eye"
                :label="t('customerSession.viewDetails')"
                class="!border-slate-400 !text-slate-200"
                @click.stop="openDetails(slotProps.data)"
              />
              <Button
                v-if="canDelete"
                type="button"
                icon="pi pi-trash"
                text
                rounded
                severity="danger"
                size="small"
                :aria-label="t('customerSession.delete.action')"
                v-tippy="{
                  content: t('customerSession.delete.action'),
                  theme: 'my-theme',
                }"
                @click.stop="confirmDelete(slotProps.data)"
              />
            </div>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">
            {{ t("customerSession.empty") }}
          </h1>
        </template>
      </DataTable>
    </div>

    <ReviewModal
      v-model:visible="modalVisible"
      :review="editingReview"
      @delete="confirmDelete"
    />
  </div>
</template>
